// @djsem/shared — Music Library Core (Prompt 05R).
// CRUD/search/filter/readiness over the canonical DJSEMLibraryTrack (P06 shape — reused,
// not duplicated). Pure in-memory collection semantics; persistence is the worker's seam.
// Delete is ARCHIVE by default (no silent data loss).

import type { DJSEMLibraryTrack } from "./smartGroups";
import { SeqCounter } from "./runtime";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "./validation";

/** A library track's readiness stage, from initial import through live approval or archival. */
export type DJSEMTrackReadiness = "imported" | "analyzed" | "prepped" | "approved" | "archived";

/** Input to {@link MusicLibraryService.createTrack}. */
export interface CreateLibraryTrackInput { preparationTrackId?: string; title: string; artist?: string; bpm?: number; key?: string; moodTags?: string[]; }
/** Input to {@link MusicLibraryService.updateTrack}. */
export interface UpdateLibraryTrackInput { track: DJSEMLibraryTrack; patch: Partial<DJSEMLibraryTrack>; }
/** Input to {@link MusicLibraryService.deleteOrArchiveTrack}. */
export interface ArchiveTrackInput { track: DJSEMLibraryTrack; hardDelete?: boolean; }
/** Input to {@link MusicLibraryService.searchTracks}. */
export interface SearchTracksInput { tracks: DJSEMLibraryTrack[]; query: string; }
/** Input to {@link MusicLibraryService.filterTracks}. */
export interface FilterTracksInput { tracks: DJSEMLibraryTrack[]; bpmMin?: number; bpmMax?: number; key?: string; tag?: string; readiness?: DJSEMTrackReadiness; }
/** Input to {@link MusicLibraryService.updateReadiness}. */
export interface UpdateTrackReadinessInput { track: DJSEMLibraryTrack; readiness: DJSEMTrackReadiness; }

const _seq = new SeqCounter();

/**
 * CRUD/search/filter/readiness operations over the canonical library track collection.
 * Pure in-memory collection semantics; persistence is the worker's seam. Deletion is
 * modeled as archival by default — never a silent, irrecoverable data loss.
 */
export class MusicLibraryService {
  /**
   * Creates a new library track in `"imported"` readiness state.
   * @param input - The track fields to create.
   * @returns The new track.
   */
  createTrack(input: CreateLibraryTrackInput): DJSEMLibraryTrack {
    return {
      preparationTrackId: input.preparationTrackId ?? `lib_${_seq.next()}`,
      title: input.title, artist: input.artist,
      bpm: input.bpm, key: input.key,
      moodTags: input.moodTags ?? [],
      reviewStatus: "imported",
    } as DJSEMLibraryTrack;
  }

  /**
   * Applies a partial patch to a track. `preparationTrackId` is immutable and ignored if
   * present in the patch; the original track is never mutated.
   * @param input - The track and the patch to apply.
   * @returns A new track with the patch applied.
   */
  updateTrack(input: UpdateLibraryTrackInput): DJSEMLibraryTrack {
    // id is immutable; everything else patchable. Never mutates the original.
    const { preparationTrackId: _ignored, ...patch } = input.patch as Record<string, unknown>;
    return { ...input.track, ...patch, preparationTrackId: input.track.preparationTrackId };
  }

  /**
   * Archives a track. `hardDelete` is modeled as archived-plus-flagged; actual row removal
   * remains a worker-level decision, never performed here.
   * @param input - The track and whether this is a hard-delete request.
   * @returns A new track with `reviewStatus: "archived"` (and `hasWarnings: true` if hard-deleted).
   */
  deleteOrArchiveTrack(input: ArchiveTrackInput): DJSEMLibraryTrack {
    if (input.hardDelete) {
      // Hard delete is modeled as archived + flagged; actual row removal is a worker decision.
      return { ...input.track, reviewStatus: "archived", hasWarnings: true } as DJSEMLibraryTrack;
    }
    return { ...input.track, reviewStatus: "archived" } as DJSEMLibraryTrack;
  }

  /**
   * Free-text searches tracks by title, artist, or mood tag (case-insensitive substring match).
   * @param input - The tracks to search and the query string.
   * @returns The matching tracks, or all tracks if the query is empty.
   */
  searchTracks(input: SearchTracksInput): DJSEMLibraryTrack[] {
    const q = input.query.trim().toLowerCase();
    if (!q) return [...input.tracks];
    return input.tracks.filter((t) =>
      (t.title ?? "").toLowerCase().includes(q) ||
      (t.artist ?? "").toLowerCase().includes(q) ||
      (t.moodTags ?? []).some((tag) => tag.toLowerCase().includes(q)));
  }

  /**
   * Filters tracks by BPM range, key, mood tag, and/or readiness state.
   * @param input - The tracks to filter and the optional filter criteria.
   * @returns The tracks matching all provided criteria.
   */
  filterTracks(input: FilterTracksInput): DJSEMLibraryTrack[] {
    return input.tracks.filter((t) => {
      if (input.bpmMin !== undefined && !(typeof t.bpm === "number" && t.bpm >= input.bpmMin)) return false;
      if (input.bpmMax !== undefined && !(typeof t.bpm === "number" && t.bpm <= input.bpmMax)) return false;
      if (input.key !== undefined && t.key !== input.key) return false;
      if (input.tag !== undefined && !(t.moodTags ?? []).includes(input.tag)) return false;
      if (input.readiness !== undefined && t.reviewStatus !== input.readiness) return false;
      return true;
    });
  }

  /**
   * Updates a track's readiness state.
   * @param input - The track and the new readiness state.
   * @returns A new track with the updated readiness.
   */
  updateReadiness(input: UpdateTrackReadinessInput): DJSEMLibraryTrack {
    return { ...input.track, reviewStatus: input.readiness } as DJSEMLibraryTrack;
  }

  /**
   * Validates a track's required fields and BPM range.
   * @param track - The track to validate.
   * @returns The validation result.
   */
  validateTrack(track: DJSEMLibraryTrack): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!track?.preparationTrackId) issues.push({ path: "$.preparationTrackId", message: "preparationTrackId required" });
    if (!track?.title) issues.push({ path: "$.title", message: "title required" });
    if (track?.bpm !== undefined && !(track.bpm > 0 && track.bpm < 400)) issues.push({ path: "$.bpm", message: "bpm out of range" });
    
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "library_track");
  }

  /**
   * Detects likely duplicate tracks by matching (title, artist) case-insensitively. Reports
   * duplicates for the caller to review — never silently merges them.
   * @param tracks - The tracks to scan.
   * @returns Groups of two-or-more track ids sharing the same (title, artist) key.
   */
  findDuplicates(tracks: DJSEMLibraryTrack[]): Array<{ key: string; ids: string[] }> {
    const map = new Map<string, string[]>();
    for (const t of tracks) {
      const key = `${(t.title ?? "").toLowerCase()}|${(t.artist ?? "").toLowerCase()}`;
      map.set(key, [...(map.get(key) ?? []), t.preparationTrackId]);
    }
    return [...map.entries()].filter(([, ids]) => ids.length > 1).map(([key, ids]) => ({ key, ids }));
  }
}
